<template>
  <div>
    <div class="card">
      <div class="bg-blue-light p-1">
        <TitleButton
          :showBtn="false"
          title="OPD Prescription Report"
        />
      </div>

      <div class="my-2 px-2">
        <DateQuerySetter
          btnTitle="Go"
          @onClickGo="getOPDBillings"
        >
          <div class="col-12 col-sm-6 col-md-5 col-lg-4">
          <label for="colFormLabel" class="col-form-label">Select Service Resource </label>
            <AsyncSelect
                placeholder="Select Service Resource"
                class="w-100"
                v-model="selectedIPDDoctor"
                :api-service="fetchServiceList"
                :additional-query="{ resource_type: 'human_resources' }"
            />
          </div>
          <div class="col-12 col-sm-6 col-md-5 col-lg-4">
            <div class="d-flex align-items-center justify-content-between filter-label">
              <label for="colFormLabel" class="col-form-label">Patient: </label>
              <span class="filter-search-icon cursor-pointer" @click="onOpenContactSearchModal">
                            <i class="fas fa-search"></i>
                        </span>
            </div>
            <AsyncSelect
              placeholder="Patient Name, ID, Mobile No"
              v-model="patient"
              :api-service="fetchContactProfiles"
              :additional-query="additionalQuery"
              :format-label="formatPatientLabel"
            />
          </div>
        </DateQuerySetter>
      </div>
      <div class="col-12">
        <div class="d-flex justify-content-end px-2">
          <button
              @click="onClickToPrint"
              class="btn btn-outline-secondary btn-sm btn-download-invoice waves-effect mx-1"
          >
            Print
          </button>
        </div>
      </div>

      <div class="col-12 px-2 my-2">
        <div class="card card-height">
          <div class="table-responsive">
            <table class="table">
              <thead>
              <tr>
                <th>Billing Date & Time</th>
                <th>Bill Number</th>
                <th>Patient Name (ID)</th>
                <th>Service Delivery Date & Time</th>
                <th>Consultant</th>
              </tr>
              </thead>
              <tbody>
              <tr v-for="(item, i) in bills" :key="i">
                <td>{{ item.bill_date }} {{ item.bill_time }}</td>
                <td>{{ item.bill_number }}</td>
                <td>{{ item.contact_profile_name }}({{ item.serial_no }})</td>
                <td>{{ item.formatted_updated_at }}</td>
                <td>{{ item.service_resource_name }}</td>
              </tr>

              </tbody>
            </table>
            <p v-if="!bills.length" class="text-center mt-5">No data</p>
          </div>
        </div>

      </div>

      <div class="px-2 position-absolute bottom-0">
        <Pagination
          :offset="offset"
          @onPageChange="onPageChange"
          ref="pagination"
        />
      </div>
    </div>
    <ContactDropdownFilter
        v-if="$store.state.isModalOpenThree"
        @onSearchContact="onSearchContact"
        type="patient"
    />
    <Loader v-if="loading"/>
  </div>
</template>

<script setup>
import TitleButton from '@/components/atom/TitleButton.vue';
import DateQuerySetter from '@/components/atom/DateQuerySetter.vue';
import {useRoute, useRouter} from "vue-router";
import {computed, inject, onMounted, reactive, ref} from "vue";
import Loader from "@/components/atom/LoaderComponent.vue";
import Pagination from "@/components/atom/Pagination.vue";
import {useStore} from "vuex";
import handleCompany from "@/services/modules/company";
import AsyncSelect from "@/components/molecule/input-field/AsyncSelect.vue";
import handleContact from "@/services/modules/contact";
import {useAsyncDropdownHelper} from "@/services/utils/asyncDropdownHelper";
import handleHospital from "@/services/modules/hospital";
import ContactDropdownFilter from '@/components/atom/Contact/ContactDropdownFilter.vue'
import opdPrescriptionReport from '@/services/utils/pdf/opdPrescriptionReport';

const $route = useRoute();
const $router = useRouter();
const $store = useStore();

const showError = inject('showError');
const showSuccess = inject('showSuccess');
const {fetchCompanyInfo} = handleCompany();
const {fetchContactProfiles} = handleContact()
const {formatPatientLabel} = useAsyncDropdownHelper()
const { exportToPDF } = opdPrescriptionReport();
const { fetchServiceList, fetchOpdPrescriptionReport } = handleHospital()
let companyInfo = reactive({})

const offset = ref(20);
const loading = ref(false);
const bills = ref([])
const prescriptionData = ref([])
const patient = ref(null)
const status = ref(null)
const company = ref({})
const selectedIPDDoctor = ref(null);
const serialNo = ref(null)
// computed
const start = computed(() => $route.query.start);
const end = computed(() => $route.query.end);
const page = computed(() => $route.query.page);
const companyId = computed(() => $route.params.companyId);

const additionalQuery = {
  type: "patient"
}

const onOpenContactSearchModal = () => {
  $store.state.isModalOpenThree = true;
}

const onSearchContact = (contact) => {
  patient.value = contact
}

async function onClickToPrint() {
  let query = getQuery();
  query += '&is_all=true'

  await fetchOpdPrescriptionReport(query).then((res) => {
    if (res.status) {
      prescriptionData.value = res
    }
  }).then(() => {
    exportToPDF(companyInfo,prescriptionData.value)
  }).catch((err) => {
    console.log("Something went wrong");
  });
}

const setPaginationData = (data) => {
  $store.commit('setPaginationData', data)
};
const resetPagination = () => {
  $store.commit('resetPagination')
};

const onPageChange = async (page) => {
  let routeQuery = Object.assign({}, $route.query)
  routeQuery.page = page
  await $router.push({path: $route.path, query: routeQuery})
  await getOPDBillings()
}
const setPagination = (data) => {
  setPaginationData({
    records: data.total,
    perPage: data.per_page,
    currentPage: data.current_page,
  })
}

const onClickAddNewButton = () => {
  let params = {
    companyId: $route.params.companyId,
    moduleId: $route.params.moduleId,
    menuId: $route.params.menuId,
    pageId: $route.params.pageId,
  }
  $router.push({
    name: 'opd-billing',
    params: params,
    query: $route.query
  })
}

const getQuery = () => {
  let query = '?company_id=' + companyId.value
  if (!start && !end) return query
  if (start) query += '&start_date=' + start.value
  if (end) query += '&end_date=' + end.value
  query += '&offset=' + offset.value
  if (page) query += '&page=' + page.value
  if (selectedIPDDoctor.value) query += '&service_resource_id=' + selectedIPDDoctor.value.id
  if (patient.value) query += '&patient_id=' + patient.value.id
  if (status.value) query += '&status=' + status.value
  return query
};

async function getOPDBillings() {
  loading.value = true;
  try {
    const res = await fetchOpdPrescriptionReport(getQuery());
    if (!res.status) {
      bills.value = []
      return $store.commit("resetPagination")
    }
    bills.value = res.data.data;
    setPagination(res.data)
  } catch (err) {
    if (err.response) {
      showError(err.response.message)
    }
  } finally {
    loading.value = false;
  }
}

const setCompanyInfo = async () => {
  await fetchCompanyInfo($route.params.companyId).then((res) => {
    companyInfo = res.data;
  }).catch((error) => {
    console.log(error);
  });
}

onMounted(() => {
  getOPDBillings()
  setCompanyInfo()
  fetchCompanyInfo(companyId.value).then(res=> {
    if(res.data) company.value = res.data;
  })
})
</script>
